import React, { useEffect, useState } from 'react';
import './SubscriptionPage.scss';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { Navigate } from 'react-router-dom';
import {
  accountTypeToText,
  getTrialDays,
  isAdminOrOwner,
  numberFormat,
} from '../../../shared/utils/utils';
import { updateCard } from '../../pricing/services/PricingService';
import usePaddle from '../../../shared/hooks/pricing/UsePaddle';
import {
  getNextPayment,
  prepareUpdatePaymentMethod,
} from '../../../shared/services/pricing/PricingService';
import { format } from 'date-fns';
import { useCreatePortalSession } from '../../../shared/api/hooks/billing/useCreatePortalSession';
import { MY_ACCOUNT } from '../../../shared/routes/path';
import { NextPaymentResponse } from '../../../shared/models/pricing/NextPaymentResponse';
import Loader from '../../../shared/components/common/loader/Loader';
import { getMonitorTypeIcon } from '../../prompt/preview/components/stats/helpers/StatsHelper';
import { ProgressBar } from 'primereact/progressbar';
import useDataLayer, { IDefaultDataLayer } from '../../../shared/hooks/global/UseDataLayer';
import { getAccount, toggleAccount } from '../../../shared/services/account/AccountService';
import { useMutation } from '@tanstack/react-query';

const SubscriptionPage: React.FC = () => {
  const { accountResponse, setAccountResponse } = useAccount();
  const { paddle } = usePaddle();
  const { t } = useTranslation();
  const { pushDataLayer } = useDataLayer();
  const [nextPaymentData, setNextPaymentData] = useState<NextPaymentResponse | null>(null);
  const { mutateAsync: createPortalSession } = useCreatePortalSession();

  const { mutate: tryToggleAccount, isPending: isToggling } = useMutation({
    mutationFn: async () => {
      await toggleAccount();
      return getAccount();
    },
    onSuccess: (freshAccountData) => {
      if (freshAccountData.isCanceledAccount) {
        sendCancelEvent();
      }
      setAccountResponse(freshAccountData);
    },
    onError: (error) => {
      console.error('Error toggling account:', error);
    },
  });

  const openUpdateCard = async () => {
    if (!paddle || !accountResponse) {
      console.error('Billing loading failed');
      return;
    }

    const transactionId = await prepareUpdatePaymentMethod();
    if (!transactionId) {
      console.error('Wrong transaction id');
      return;
    }

    updateCard(paddle, transactionId, accountResponse);
  };

  const sendCancelEvent = () => {
    if (!accountResponse) {
      console.error('Cancel event failed');
      return;
    }

    pushDataLayer<IDefaultDataLayer>({
      event: 'subscription_canceled',
      email_hashed: accountResponse.email,
      user_id: accountResponse.userId.toString(),
      account_id: accountResponse.accountId.toString(),
    });
  };

  useEffect(() => {
    if (accountResponse?.accountType === 'TRIAL') {
      setNextPaymentData({ monthlyPrice: 0, discount: null });
      return;
    }
    getNextPayment().then((nextPayment) => {
      setNextPaymentData(nextPayment);
    });
  }, []);

  if (nextPaymentData === null && !accountResponse?.isSuperAdmin) {
    return <Loader />;
  }

  if (!isToggling && (!accountResponse || !isAdminOrOwner(accountResponse.role))) {
    return <Navigate to={MY_ACCOUNT} replace />;
  }

  if (!accountResponse) {
    return <Loader />;
  }

  const isPaypal = accountResponse.paymentMethod === 'Paypal';

  return (
    <div className="subscription-page">
      {accountResponse.paymentSuspended && (
        <div className="subscription-page__payment-suspended-info">
          {t('paymentSuspendedSubscriptionInfo')}
        </div>
      )}

      <div className="subscription-page__content">
        <div className="subscription-page__content-header">
          <label>{t('currentPlan')}</label>
          <div className="subscription-page__content-header-actions">
            {accountResponse.customerId && (
              <Button
                className={`btn btn--sm  btn--primary`}
                label={t('manageSubscription')}
                onClick={async () => {
                  try {
                    const subscriptionIds = accountResponse.subscriptionId
                      ? [accountResponse.subscriptionId]
                      : [];
                    const { url } = await createPortalSession(subscriptionIds);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    console.error('Failed to create portal session:', error);
                  }
                }}
              />
            )}
            {!accountResponse.subscriptionId &&
              !accountResponse.customerId &&
              accountResponse.isCanceledAccount && (
                <Button
                  className="btn btn--sm btn--primary"
                  label={t('renewAccount')}
                  onClick={() => tryToggleAccount()}
                />
              )}
          </div>
        </div>
        <div className="subscription-page__content-box">
          <div className="settings-tile">
            {accountResponse.accountType !== 'TRIAL' &&
            !accountResponse.isCanceledAccount &&
            nextPaymentData?.monthlyPrice ? (
              <>
                <div className="settings-tile__title">
                  {t(
                    accountResponse.billingInterval === 'year'
                      ? 'nextAnnuallyPaymentCost'
                      : 'nextMonthlyPaymentCost'
                  )}
                </div>
                <div className="settings-tile__value">
                  ${numberFormat().format(nextPaymentData?.monthlyPrice / 100)} ({t('exclVAT')})
                </div>
                <div className="settings-tile__tag">
                  {accountTypeToText(accountResponse.accountType)}
                </div>
              </>
            ) : (
              <>
                <div className="settings-tile__title">{t('cost')}</div>
                <div className="settings-tile__value">{t('free')}</div>
                <div className="settings-tile__tag">
                  {accountTypeToText(accountResponse.accountType)}
                </div>
              </>
            )}
          </div>

          <div className="settings-tile">
            {accountResponse.accountType !== 'TRIAL' &&
            ['PAID', 'TRIAL', 'OVERDUE'].includes(accountResponse.accountStatus) ? (
              <>
                <div className="settings-tile__title">
                  {accountResponse.isCanceledAccount ? t('cancelationDate') : t('nextPayment')}:
                </div>
                <div className="settings-tile__value">
                  {format(accountResponse.endSubscription, 'yyyy-MM-dd ')}
                </div>
              </>
            ) : (
              <>
                <div className="settings-tile__title">{t('trialDays')}:</div>
                <div className="settings-tile__value">{getTrialDays(accountResponse)}</div>
              </>
            )}
          </div>

          {accountResponse.accountType !== 'TRIAL' &&
            !accountResponse.isCanceledAccount &&
            nextPaymentData?.discount &&
            nextPaymentData?.discount.status === 'active' && (
              <div className="settings-tile">
                <div className="settings-tile__title">{t('discount')}</div>
                <div className="settings-tile__value">
                  {nextPaymentData?.discount?.type === 'percentage'
                    ? `${nextPaymentData?.discount?.amount}%`
                    : `$${Number(nextPaymentData?.discount?.amount) / 100}`}
                </div>
              </div>
            )}
        </div>
      </div>

      {accountResponse.paymentMethod && accountResponse.hasSubscription && (
        <div className="subscription-page__content">
          <div className="subscription-page__content-header">
            <label>{t('paymentMethod')}</label>
            <div className="subscription-page__content-header-actions">
              <Button
                className="btn btn--sm "
                label={t('changePaymentMethod')}
                onClick={() => openUpdateCard()}
              />
            </div>
          </div>
          <div className="subscription-page__content-box">
            <div className="settings-tile">
              <div className="settings-tile__title">{t('paymentMethod')}</div>
              <div className="settings-tile__value">
                {isPaypal ? accountResponse.paymentMethod : `**** ${accountResponse.paymentMethod}`}
              </div>
            </div>
            {!isPaypal && accountResponse.expiryCardMonth && accountResponse.expiryCardYear && (
              <div className="settings-tile">
                <div className="settings-tile__title">{t('cardExpire')}</div>
                <div className="settings-tile__value">{`${accountResponse.expiryCardMonth}/${accountResponse.expiryCardYear}`}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="subscription-page__content">
        <div className="subscription-page__content-header">
          <label>{t('usage')}</label>
        </div>
        <div className="subscription-page__content-box">
          <div className="settings-tile settings-tile--big">
            <div className="settings-tile__title">{t('questionLimits')}</div>
            <div className="settings-tile__value">{`${numberFormat().format(
              accountResponse.createdMonitorCount
            )} ${t('of')} ${numberFormat().format(accountResponse.maxMonitorCount)}`}</div>
            <ProgressBar
              className="progress-bar progress-bar--mini"
              value={(accountResponse.createdMonitorCount * 100) / accountResponse.maxMonitorCount}
            />
          </div>

          <div className="settings-tile settings-tile--big">
            <div className="settings-tile__title">{t('userLimits')}</div>
            <div className="settings-tile__value">{`${numberFormat().format(
              accountResponse.usersOnAccount
            )} ${t('of')} ${numberFormat().format(accountResponse.maxUsers)}`}</div>
            <ProgressBar
              className="progress-bar progress-bar--mini"
              value={(accountResponse.usersOnAccount * 100) / accountResponse.maxUsers}
            />
          </div>

          <div className="settings-tile settings-tile--big">
            <div className="settings-tile__title">{t('activeModels')}</div>
            <div className="settings-tile__block">
              {accountResponse.features.map((feature) => (
                <span key={feature} className={`${getMonitorTypeIcon(feature)} ico--md`} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {!accountResponse.subscriptionId && !accountResponse.isCanceledAccount && (
        <div className="subscription-page__delete-account">
          <Button
            className="btn btn--link btn--link-blue"
            label={t('startCancellation')}
            onClick={() => tryToggleAccount()}
          />
          <span className="subscription-page__delete-account-question">
            {t('cancellationProcessSuffix')}
          </span>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
